import { createFileRoute, redirect } from '@tanstack/react-router'
import { apiClient } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { LoginPage } from '@/features/auth/LoginPage'

interface RawMeResponse {
  id: number
  email: string
  username: string
  role: string
  status: string
  user_code: string
}

type LoginSearch = {
  returnTo?: string
  /**
   * Optional bootstrap-error marker (Story E02.05 AC-05). When the
   * `_authenticated` bootstrap loader fails with a network/5xx error
   * (not 401), it redirects here with `error=network` so the page can
   * surface "сервер недоступний" inline. Permissive type — any other
   * value is treated as undefined.
   */
  error?: 'network'
}

/**
 * Public login route.
 *
 * Story E02.01:
 *   - AC-06 preflight-redirect: when an already-authenticated user lands on
 *     `/login` directly, we throw `redirect({ to: '/dashboard' })` from
 *     `beforeLoad` so the form never mounts (avoids the visible flash that
 *     a useEffect-based redirect would cause).
 *   - AC-07 search params: `validateSearch` extracts an optional `returnTo`.
 *     Anti-open-redirect normalization happens at navigation-time inside
 *     `LoginPage` via `safeReturnTo` — keeping the route schema permissive
 *     means stale links (`/login?returnTo=//evil.com`) don't 404, they just
 *     fall back to `/dashboard` after login.
 */
export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    returnTo:
      typeof search.returnTo === 'string' ? search.returnTo : undefined,
    error: search.error === 'network' ? 'network' : undefined,
  }),
  beforeLoad: async () => {
    // Fast-path: in-memory store says authenticated (warm reload, SPA nav).
    if (useAuthStore.getState().isAuthenticated) {
      throw redirect({ to: '/dashboard' })
    }
    // Cold-mount probe: the auth cookie is httpOnly so we can't read it
    // from JS, but a successful GET /api/auth/me means the cookie is alive
    // and the user is really logged in — they just landed on /login fresh.
    // Without this probe the user types into a login form despite already
    // having a valid session, gets 401 from a forgotten password, and
    // chews through the 3-attempt rate limit. See request_id sequence in
    // prod logs 2026-05-12 07:25 — interleaved /login 401 + /refresh 200.
    try {
      const { data } = await apiClient.get<RawMeResponse>('/auth/me')
      useAuthStore.getState().setUser({
        username: data.username,
        realUsername: data.username,
        role: data.role === 'admin' ? 'admin' : 'user',
        userCode: data.user_code ?? '',
        activeWorkspaceId: null,
        workspaceName: null,
      })
      throw redirect({ to: '/dashboard' })
    } catch (err) {
      // TanStack router throws `redirect()` — rethrow so navigation fires
      // instead of being swallowed as "auth probe failed".
      if (err && typeof err === 'object' && 'href' in err) throw err
      // 401 / network — proceed to login form as before.
    }
  },
  component: LoginRouteComponent,
})

function LoginRouteComponent() {
  const { returnTo } = Route.useSearch()
  return <LoginPage returnTo={returnTo} />
}
